import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ValueType } from 'react-select';

import type { Billables } from '@feathr/blackbox';
import { Billable } from '@feathr/blackbox';
import { Select } from '@feathr/components';

import * as styles from './BillableSelect.css';

export interface IBillableSelectProps {
  label?: React.ReactNode;
  isLoading?: boolean;
  onChange?: (value: ValueType<Billable>) => void;
  placeholder?: string;
  value?: Billable;
}

interface IBillableSelectBaseProps extends IBillableSelectProps {
  billables: Billables;
  primaryBillableId?: string;
}

function getOptionLabel(option: Billable): string {
  const description = option.get('description');
  if (description) {
    return description;
  }

  const name = option.get('name');
  // Exception for no default option
  if (option.id === 'nodefault') {
    return name;
  }

  const email = option.get('email', 'no email address');
  return `${name} (${email})`;
}

function isOptionDisabled(option: Billable): boolean {
  return option.id === 'nodefault';
}

/**
 * `BillableSelect` without account-context hooks: the caller supplies the billables collection and
 * the account's default billable, so Perch can pass another account's.
 */
function BillableSelectBase({
  billables,
  isLoading = false,
  label,
  onChange,
  placeholder,
  primaryBillableId,
  value,
}: Readonly<IBillableSelectBaseProps>): JSX.Element {
  const { t } = useTranslation();

  const primaryBillable = primaryBillableId ? billables.get(primaryBillableId) : undefined;

  const filters = {};
  if (primaryBillableId) {
    filters['id__ne'] = primaryBillableId;
  }
  const otherBillables = billables.list({ filters });

  function handleChange(changedValue: ValueType<Billable>): void {
    if (Array.isArray(changedValue)) {
      return;
    }
    onChange?.(changedValue);
  }

  const options = [
    {
      label: t('Default'),
      options: [],
    },
    {
      label: t('Other'),
      options: otherBillables.models.slice(),
    },
  ];

  if (primaryBillable) {
    options[0].options.push(primaryBillable);
  } else {
    options[0].options.push(
      // Add mock billable when no default billing is available
      new Billable({
        // isOptionDisabled matches this fake id to disable the option.
        id: 'nodefault',
        name: t('No default billing available'),
      }),
    );
  }

  return (
    <div className={styles.root}>
      <Select<Billable>
        getOptionLabel={getOptionLabel}
        isLoading={otherBillables.isPending || isLoading}
        isOptionDisabled={isOptionDisabled}
        label={label}
        name={'billable-select'}
        onChange={handleChange}
        options={options}
        placeholder={placeholder ?? t('Select a billing configuration')}
        value={value}
      />
    </div>
  );
}

export default observer(BillableSelectBase);
